import {Inject, Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {of} from 'rxjs';
import {catchError, finalize, tap} from 'rxjs/operators';
import { merge } from 'es-toolkit';
import * as print from "@arcgis/core/rest/print";
import {BaseService} from '../../../../shared/src/lib/services/base.service';
import {MapService} from '../../../../shared/src/lib/services/map.service';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';

interface ExportWebMap {
  mapOptions: {
    extent: any;
    scale: number;
    rotation: number;
    spatialReference: any
  },
  operationalLayers: any[],
  baseMap: any[]
}

function exportWebMapJSON(view: MapView): ExportWebMap {
  // @ts-ignore
  const mapJson = view.map.toJSON();
  mapJson.operationalLayers = mapJson.operationalLayers.filter(l => !('visibility' in l) || l.visibility);
  return {
    mapOptions: {
      extent: view.extent.toJSON(),
      scale: view.scale,
      rotation: view.rotation,
      spatialReference: view.spatialReference.toJSON()
    },
    ...mapJson
  }
}
@Injectable({
  providedIn: 'root'
})
export class SolidWasteService extends BaseService {
  item: any = {};
  meta: any = {};

  complaintsService: BaseService;

  constructor(http: HttpClient, private mapService: MapService, @Inject('environment') private environment) {
    super(`${environment.baseUrl}/solidwaste/facilities`, http);
    this.complaintsService = new BaseService(`${environment.baseUrl}/solidwaste/complaints`, http)
  }

  public loadData(searchObj) {
    return this.getList(searchObj).pipe(
      tap(response => {
        if (response.results[0]) {
          merge(this.item, response.results[0]);
        } else {
          this.item = {};
        }
        merge(this.meta, response.meta);

        this.meta.display_fields = ['previously_known_as', 'doing_business_as'];
        this.meta.type = 'solidwaste/facilities';
//                this.meta.autocomplete = {
//                    "field": 'name',
//                    "url": "/api/solidwaste/facilities/?search=",
//                    "foreign_key": "site"
//                };
        this.meta.urlPath = 'solidwaste';
//                this.meta.featureService = 'https://it.innovateteam.com/arcgis/rest/services/Guam/SolidWaste/FeatureServer/0';
        this.meta.subItems = ['complaints', 'permit', 'attachments'];
        this.meta.fields.complaints.display_fields = [
          'number', 'received_datetime', 'received_by', 'assign_to', 'assigned_datetime',
          'complainant_name', 'complainant_address', 'complainant_phone', 'is_duplicate', 'illegal_dumping', 'hard_filling',
          'abandon_vehicle', 'littering', 'oil_spil', 'illegal_storage_burning', 'other_violation', 'status'
        ];
        this.meta.fields.complaints.type = 'solidwaste/complaints';
        this.meta.fields.complaints.print = {
          label: 'Print',
          tooltip: 'Generate Permit',
          get: (item) => {
            return new Promise<void>((resolve, reject) => {
              // this.mapService.getView().then(view => {
              const mapjson = JSON.stringify(exportWebMapJSON(this.mapService.getView()))
              this.http.post(
                `${this.complaintsService.baseUrl}/${item.globalid}/report/`,
                {mapjson}, {responseType: 'blob'}).pipe(
                tap(r => {
                  console.log(r);
                  // @ts-ignore
                  const file = new Blob([r], {type: 'application/pdf'});
                  const fileURL = URL.createObjectURL(file);
                  resolve();
                  window.open(fileURL, '_blank');
                }),
                catchError(() => {
                  reject();
                  return of();
                })
              ).subscribe()
            });
          }
        };
        this.meta.fields.complaints.verbose_name_plural = 'Complaints';
        this.meta.fields.complaints.verbose_name = 'Complaint';
        this.meta.fields.complaints.foreign_key_field = 'solidwastefacility';

        this.meta.fields.complaints.subItems = ['inspections', 'enforcement_actions', 'note'];
        this.meta.fields.complaints.fields = this.meta.fields.complaints.child.fields;
        this.meta.fields.complaints.fields.duplicate_complaint.get_choices = (currentItem) => {
          return new Promise(resolve => {
            this.complaintsService.getList<any>(searchObj).pipe(tap(r => {
              if (r.results.length === 0) {
                resolve([{
                  display_name: 'No other complaints entered for this site.',
                  value: null
                }]);
              } else {
                let currentItemIndex = -1;
                const choices = r.results.map((complaint, index) => {
                  if (currentItem && complaint.globalid === currentItem.globalid) {
                    currentItemIndex = index;
                  }
                  return {display_name: complaint.number, value: complaint.globalid};
                });
                if (currentItemIndex > -1) choices.splice(currentItemIndex, 1);
                resolve(choices);
              }
            }));
          });
        };

        this.meta.fields.complaints.note = {fields: this.meta.fields.complaints.child.fields.note.child.fields};
        this.meta.fields.complaints.note.verbose_name_plural = 'Notes';
        this.meta.fields.complaints.note.verbose_name = 'Note';
        this.meta.fields.complaints.note.display_fields = [
          'text', 'created_date', 'created_user', 'last_edited_date', 'last_edited_user'
        ];
        this.meta.fields.complaints.note.foreign_key_field = 'complaint';
        this.meta.fields.complaints.note.type = 'solidwaste/notes';
        this.meta.fields.complaints.note.deleteText = 'Are you sure you want to delete this note?';

        this.meta.fields.complaints.inspections = {
          type: 'solidwaste/inspections',
          display_fields: [
            'type', 'inspection_datetime', 'person_interviewed', 'phone', 'description', 'outcome'
          ],
          subItems: ['inspection_attachment'],
          foreign_key_field: 'complaint',
          inspection_attachment: {type: 'solidwaste/inspectionattachments'},
        };
        this.meta.fields.complaints.enforcement_actions = {
          type: 'solidwaste/enforcementactions',
          display_fields: [
            'type', 'issued_datetime', 'compliance_deadline', 'final_day_to_submit_noi',
            'date_received_noi', 'hearing_date',
            'cost_per_violation', 'num_of_violations', 'total_fine', 'settlement', 'paid', 'paid_datetime',
            'returned_to_compliance'
          ],
          subItems: ['notes'],
          foreign_key_field: 'complaint',
          notes: {
            type: 'solidwaste/notes',
            foreign_key_field: 'enforcement_action'
          }
        };
        //
        // this.meta.fields.inspection.complaint = items.meta.fields.inspection.child.fields.complaint;
        // this.meta.fields.inspection.complaint.fields = items.meta.fields.inspection.child.fields.complaint.fields;
        // this.meta.fields.inspection.complaint.path = 'solidwaste/complaints';
        // this.meta.fields.inspection.complaint.get_choices = function (current_item) {
        //   let deferred = $q.defer();
        //   let thisSearchObj = angular.copy(searchObj);
        //   thisSearchObj.is_duplicate = false;
        //   Restangular.all('solidwaste/complaints').getList(thisSearchObj).then(function (response) {
        //     if (response.length === 0) {
        //       deferred.resolve([{
        //         display_name: "No complaints entered for this site.",
        //         value: null
        //       }]);
        //     }
        //     else {
        //       let current_item_index = -1;
        //       let choices = response.map(function (complaint, index) {
        //         if (current_item && complaint.globalid === current_item.globalid) {
        //           current_item_index = index;
        //         }
        //         return {display_name: complaint.number, value: complaint.globalid};
        //       });
        //       if (current_item_index > -1) choices.splice(current_item_index, 1);
        //       deferred.resolve(choices);
        //     }
        //   });
        //   return deferred.promise;
        // };


        // permits

        this.meta.fields.permit.display_fields = [
          'number', 'reviewer', 'quarter_received', 'type', 'sub_type', 'applicant_name',
          'applicant_title', 'received_date', 'submission_date', 'application_type', 'fee_paid', 'status',
          'issued_datetime', 'expiration_datetime', 'office_status'
        ];
        this.meta.fields.permit.type = 'solidwaste/permits';
        this.meta.fields.permit.foreign_key_field = 'solidwastefacility';
        this.meta.fields.permit.verbose_name = 'Permit';
        this.meta.fields.permit.verbose_name_plural = 'Permits';
        this.meta.fields.permit.subItems = ['vehicle', 'waste', 'note'];
        this.meta.fields.permit.waste = {
          // fields: this.meta.fields.permit.child.fields.waste.child.fields,
          display_fields: ['type', 'amount', 'units', 'frequency'],
          type: 'solidwaste/waste',
          foreign_key_field: 'permit',
          verbose_name: 'Waste'
        };
        this.meta.fields.permit.vehicle = {
          // fields: this.meta.fields.permit.child.fields.vehicle.child.fields,
          verbose_name_plural: 'Vehicles',
          verbose_name: 'Vehicle',
          display_fields: ['make', 'model', 'year', 'license_plate'],
          type: 'solidwaste/vehicles',
          foreign_key_field: 'permit'
        };

        this.meta.fields.permit.note = {
          // fields: this.meta.fields.permit.child.fields.note.child.fields,
          verbose_name_plural: 'Notes',
          verbose_name: 'Note',
          display_fields: [
            'text', 'created_date', 'created_user', 'last_edited_date', 'last_edited_user'
          ],
          type: 'solidwaste/notes',
          foreign_key_field: 'permit'
        };

        this.meta.fields.attachments = {type: 'solidwaste/facilityattachments'};

      })
    );
  }
}
